'use client';

import React from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faPeopleGroup, faFileInvoice } from '@fortawesome/pro-solid-svg-icons';
import { useFlags } from 'launchdarkly-react-client-sdk';
import useTableFilterSelections from 'src/hooks/filters/useTableFilterSelections';
import { EncountersInfoTooltip } from 'src/components/EncountersInfoTooltip';
import { CLAIMS_LABEL, claimsMetricLabel } from 'src/utils/encounterLabels';

const options = [
  { label: CLAIMS_LABEL, value: 'claims', icon: faFileInvoice },
  { label: 'Patients', value: 'patients', icon: faPeopleGroup },
] as const;

interface NewVolumeTypeToggleProps {
  location?: string;
}

const NewVolumeTypeToggle: React.FC<NewVolumeTypeToggleProps> = ({
  location = 'lists',
}): React.ReactElement => {
  const { type, setType, cohort_id } = useTableFilterSelections(location);
  const { showEncountersTotals } = useFlags();

  // A cohort column reads the encounters metric behind the flag, so the claims side of
  // the toggle is labeled "Encounters" to match the value it drives.
  const isEncounters = !!showEncountersTotals && !!cohort_id;
  const claimsLabel = claimsMetricLabel(!!cohort_id, !!showEncountersTotals);

  const handleClick = (value: 'claims' | 'patients') => {
    setType(value);
  };

  return (
    <div className="inline-flex flex-col gap-1 w-full">
      <div className="text-primary text-xs font-semibold leading-none mb-1 flex items-center gap-1">
        Type
        {isEncounters && <EncountersInfoTooltip />}
      </div>
      <div className="relative w-full h-10 select-none">
        {/* Background pill */}
        <div className="absolute inset-0 bg-level-2 rounded-full" />
        {/* Active pill */}
        <div
          className="absolute top-0 bottom-0 w-1/2 transition-all duration-300 rounded-full bg-sky-600 shadow-xl z-10"
          style={{
            left: type === 'claims' ? '0%' : '50%',
          }}
        />
        <div className="relative flex w-full h-full z-20">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`flex-1 px-4 py-2.5 transition-colors duration-300 rounded-full font-['Inter'] text-sm leading-tight focus:outline-none flex items-center justify-center gap-2
                ${
                  type === option.value
                    ? 'text-white font-bold'
                    : 'text-secondary font-normal'
                }
              `}
              aria-pressed={type === option.value}
              onClick={() => handleClick(option.value)}
              tabIndex={0}
              data-testid={`volume-type-toggle-${option.value}`}
              style={{ minWidth: 0, zIndex: 30 }}
            >
              <Icon
                icon={option.icon}
                className={`w-4 h-4 ${
                  type === option.value ? 'text-white' : 'text-secondary'
                }`}
              />
              {option.value === 'claims' ? claimsLabel : option.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default NewVolumeTypeToggle;
